import React from 'react';
import Card from '@/components/ui/Card';
import Button from '@/components/ui/Button';
import { formatDate, formatRelativeTime } from '@/utils';
import type { LetterRecord } from '@/types/letters';
import {
  RiMailAddLine as MailAddIcon,
  RiMailLine as MailIcon,
  RiTimeLine as TimeIcon,
  RiFileTextLine as TextIcon,
  RiMenuLine as MenuIcon,
  RiListCheck2 as ListIcon,
  RiUserLine as UserIcon,
  RiTeamLine as TeamIcon,
  RiTrophyLine as TrophyIcon,
} from 'react-icons/ri';

function formatLetterType(t?: string) {
  if (!t) return 'Letter';
  const map: Record<string, string> = {
    recommendation: 'Recommendation',
    cover_letter: 'Cover letter',
    petition: 'Petition',
    other: 'Other',
  };
  return map[t] || t.replace(/_/g, ' ').replace(/\b\w/g, s => s.toUpperCase());
}

// Helper functions to extract metadata
function getRecommenderName(letter: LetterRecord): string {
  return letter.metadata?.recommender_name || letter.metadata?.referee_name || '-';
}

function getRecommenderBackground(letter: LetterRecord): string {
  // Check if file was uploaded instead of text
  const hasFile = (letter.metadata?.file_ids && letter.metadata.file_ids.length > 0) || !!letter.metadata?.referee_document_id;
  const hasText = letter.metadata?.recommender_background;

  if (hasFile && !hasText) {
    return '📎 File uploaded';
  }
  return letter.metadata?.recommender_background || '';
}

function getRelationship(letter: LetterRecord): string {
  const hasFile = (letter.metadata?.file_ids && letter.metadata.file_ids.length > 0) || !!letter.metadata?.referee_document_id;
  const hasText = letter.metadata?.relationship_to_beneficiary;

  if (hasFile && !hasText) {
    return '📎 File uploaded';
  }
  return letter.metadata?.relationship_to_beneficiary || '';
}

function getAchievements(letter: LetterRecord): string {
  const hasFile = (letter.metadata?.file_ids && letter.metadata.file_ids.length > 0) || !!letter.metadata?.referee_document_id;
  const hasText = letter.metadata?.achievements_to_highlight;

  if (hasFile && !hasText) {
    return '📎 File uploaded';
  }
  return letter.metadata?.achievements_to_highlight || '';
}

function getCriteria(letter: LetterRecord): string[] {
  return letter.metadata?.selected_criteria || [];
}

function truncateText(text: string, maxLength: number = 50): string {
  if (!text || text.length <= maxLength) return text || '-';
  return text.substring(0, maxLength) + '...';
}

interface LettersListProps {
  letters: LetterRecord[];
  loading?: boolean;
  onCreate: () => void;
  onOpen: (letter: LetterRecord) => void;
  onDelete: (letter: LetterRecord) => Promise<void>;
}

const LettersList: React.FC<LettersListProps> = ({
  letters,
  loading,
  onCreate,
  onOpen,
  onDelete,
}) => {
  const [deletingId, setDeletingId] = React.useState<string | null>(null);

  const handleDelete = async (letter: LetterRecord) => {
    const confirmed = window.confirm(
      'Are you sure you want to delete this letter? This action cannot be undone.'
    );
    if (!confirmed) return;
    try {
      setDeletingId(letter.id);
      await onDelete(letter);
    } finally {
      setDeletingId(null);
    }
  };
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-neutral-900">
          Recommendation Letters
        </h3>
        <Button onClick={onCreate} className="inline-flex items-center gap-2">
          <MailAddIcon className="h-4 w-4" />
          <span>Create Letter</span>
        </Button>
      </div>

      {loading ? (
        <Card className="py-12 text-center text-neutral-500">
          Loading letters…
        </Card>
      ) : letters.length === 0 ? (
        <Card className="py-12 text-center">
          <MailIcon className="h-12 w-12 text-neutral-300 mx-auto mb-3" />
          <h4 className="text-neutral-900 font-medium mb-1">No letters yet</h4>
          <p className="text-neutral-600 text-sm mb-4">
            Create your first recommendation letter for this case.
          </p>
          <Button onClick={onCreate} className="inline-flex items-center gap-2">
            <MailAddIcon className="h-4 w-4" />
            <span>Create letter</span>
          </Button>
        </Card>
      ) : (
        <div className="bg-white shadow overflow-x-auto rounded-lg border border-neutral-200">
          <div className="hidden lg:block">
            <table className="min-w-full divide-y divide-neutral-200">
              <thead className="bg-neutral-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                    Recommender
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                    Recommender's Background
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                    Relationship to Beneficiary
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                    Achievements to Highlight
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                    Criteria
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-neutral-200">
                {letters.map(letter => {
                  const criteria = getCriteria(letter);
                  const recommenderBg = getRecommenderBackground(letter);
                  const relationship = getRelationship(letter);
                  const achievements = getAchievements(letter);

                  return (
                    <tr
                      key={letter.id}
                      onClick={() => onOpen(letter)}
                      className="hover:bg-neutral-50 transition-colors cursor-pointer"
                    >
                      <td className="px-4 py-4 whitespace-nowrap text-sm font-medium text-neutral-900">
                        {getRecommenderName(letter)}
                      </td>
                      <td className="px-4 py-4 text-sm text-neutral-700 max-w-xs">
                        <div className="flex items-start gap-1" title={recommenderBg}>
                          <MenuIcon className="h-4 w-4 text-neutral-400 flex-shrink-0 mt-0.5" />
                          <span className="truncate">{truncateText(recommenderBg, 40)}</span>
                        </div>
                      </td>
                      <td className="px-4 py-4 text-sm text-neutral-700 max-w-xs">
                        <div className="flex items-start gap-1" title={relationship}>
                          <MenuIcon className="h-4 w-4 text-neutral-400 flex-shrink-0 mt-0.5" />
                          <span className="truncate">{truncateText(relationship, 40)}</span>
                        </div>
                      </td>
                      <td className="px-4 py-4 text-sm text-neutral-700 max-w-xs">
                        <div className="flex items-start gap-1" title={achievements}>
                          <MenuIcon className="h-4 w-4 text-neutral-400 flex-shrink-0 mt-0.5" />
                          <span className="truncate">{truncateText(achievements, 30)}</span>
                        </div>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-neutral-700">
                        {criteria.length > 0 ? (
                          <div className="flex items-center gap-1">
                            <span className="text-neutral-900 font-medium">
                              {criteria[0].substring(0, 20)}{criteria[0].length > 20 ? '...' : ''}
                            </span>
                            {criteria.length > 1 && (
                              <span className="text-neutral-500 font-medium">
                                +{criteria.length - 1}
                              </span>
                            )}
                          </div>
                        ) : (
                          <span className="text-neutral-400">-</span>
                        )}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm" onClick={(e) => e.stopPropagation()}>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => onOpen(letter)}
                          className="text-green-600 border-green-200 hover:bg-green-50"
                        >
                          View Letter
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Mobile & Tablet cards */}
          <div className="lg:hidden divide-y divide-neutral-200">
            {letters.map(letter => {
              const criteria = getCriteria(letter);
              const recommenderBg = getRecommenderBackground(letter);
              const relationship = getRelationship(letter);
              const achievements = getAchievements(letter);

              return (
                <div key={letter.id} className="p-4 space-y-3">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <UserIcon className="h-4 w-4 text-primary-600" />
                        <h4 className="font-semibold text-neutral-900 text-sm">
                          {getRecommenderName(letter)}
                        </h4>
                      </div>
                      <div className="flex items-center gap-1">
                        <TimeIcon className="h-3 w-3 text-neutral-400" />
                        <span className="text-xs text-neutral-500">
                          {formatRelativeTime(
                            letter.updated_at ||
                              letter.created_at ||
                              new Date().toISOString()
                          )}
                        </span>
                      </div>
                    </div>
                    <span
                      className={`px-1.5 py-0.5 rounded border text-[10px] ${
                        letter.status === 'ready'
                          ? 'bg-green-50 text-green-700 border-green-200'
                          : letter.status === 'processing'
                            ? 'bg-yellow-50 text-yellow-700 border-yellow-200'
                            : 'bg-neutral-50 text-neutral-700 border-neutral-200'
                      }`}
                    >
                      {letter.status}
                    </span>
                  </div>

                  {/* Metadata display */}
                  <div className="space-y-2 text-xs">
                    {recommenderBg && (
                      <div>
                        <div className="text-neutral-500 font-medium mb-0.5">Background:</div>
                        <div className="text-neutral-700">{truncateText(recommenderBg, 80)}</div>
                      </div>
                    )}
                    {relationship && (
                      <div>
                        <div className="text-neutral-500 font-medium mb-0.5">Relationship:</div>
                        <div className="text-neutral-700">{truncateText(relationship, 80)}</div>
                      </div>
                    )}
                    {achievements && (
                      <div>
                        <div className="text-neutral-500 font-medium mb-0.5">Achievements:</div>
                        <div className="text-neutral-700">{truncateText(achievements, 80)}</div>
                      </div>
                    )}
                    {criteria.length > 0 && (
                      <div>
                        <div className="text-neutral-500 font-medium mb-0.5">Criteria:</div>
                        <div className="text-neutral-700">
                          {criteria.length} selected
                          {criteria.length > 0 && (
                            <span className="text-neutral-400 ml-1">
                              ({criteria.slice(0, 2).join(', ')}{criteria.length > 2 ? '...' : ''})
                            </span>
                          )}
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="pt-2 border-t border-neutral-100">
                    <div className="grid grid-cols-2 gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => onOpen(letter)}
                        className="text-green-600 border-green-200 hover:bg-green-50"
                      >
                        View Letter
                      </Button>
                      <Button
                        size="sm"
                        variant="outline-danger"
                        onClick={() => handleDelete(letter)}
                        disabled={deletingId === letter.id}
                      >
                        {deletingId === letter.id ? 'Deleting…' : 'Delete'}
                      </Button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default LettersList;
